import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { weekdayOf } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { monthDays } from '../../db/diary.js';
import type { MonthDay } from '../../db/diary.js';
import { formatDay } from '../../format.js';
import { useTimeZone, useToday } from '../../hooks/useToday.js';
import { useSwipePager } from '../../gestures/SwipeProvider.js';

/** Füllstärke je Stimmung (1–5) — heller ist schlechter, nie rot. */
const MOOD_FILL = ['', 'bg-good/10', 'bg-good/25', 'bg-good/40', 'bg-good/60', 'bg-good/85'];

function shiftMonth(month: string, step: number): string {
  const [year, mon] = month.split('-').map(Number);
  const index = (year ?? 2026) * 12 + ((mon ?? 1) - 1) + step;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/**
 * Der Monat (18.10.2026): jeder Tag eine Fläche, gefüllt nach Stimmung (sonst
 * Energie), ein Punkt fürs Training, ein Kringel für Fotos oder Gedanken.
 * Antippen öffnet den Tag. Ein Tag ohne Angabe bleibt leer — keine Farbe, die
 * eine Antwort vortäuscht.
 */
export function MonthView({ onOpen }: { onOpen: (date: string) => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const today = useToday();
  const [month, setMonth] = useState<string | null>(null);
  const shown = month ?? today?.slice(0, 7) ?? null;
  const days = useLiveQuery(
    () => (shown && timeZone ? monthDays(shown, timeZone, db) : Promise.resolve([])),
    [shown, timeZone],
    [] as MonthDay[],
  );
  // Wischen auf dem Monat blättert (24.10.2026) — nie über den laufenden hinaus.
  const pager = useSwipePager<HTMLElement>(
    () => shown && setMonth(shiftMonth(shown, -1)),
    () => shown && setMonth(shiftMonth(shown, 1)),
    () => !!shown && !!today && shown < today.slice(0, 7),
  );

  if (!shown || !today) return <></>;

  const lead = days.length > 0 ? weekdayOf(days[0]!.date) - 1 : 0;
  const title = new Date(`${shown}-15T12:00:00`).toLocaleDateString(i18n.language, {
    month: 'long',
    year: 'numeric',
  });
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    formatDay(`2026-10-${String(5 + index).padStart(2, '0')}`, 'weekdayShort', i18n.language),
  );

  return (
    <section aria-labelledby="month-heading" className="kw-tile flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setMonth(shiftMonth(shown, -1))}
          aria-label={t('diary.previousMonth')}
          className="kw-btn kw-btn-quiet kw-btn-small px-3"
        >
          ←
        </button>
        <h2 id="month-heading" className="font-semibold">
          {title}
        </h2>
        <button
          type="button"
          onClick={() => setMonth(shiftMonth(shown, 1))}
          disabled={shown >= today.slice(0, 7)}
          aria-label={t('diary.nextMonth')}
          className="kw-btn kw-btn-quiet kw-btn-small px-3"
        >
          →
        </button>
      </div>
      {/* Nur das Raster gleitet — Pfeile und Monat oben bleiben stehen. */}
      <div ref={pager} className="flex flex-col gap-3">
        <ol className="grid grid-cols-7 gap-1" aria-label={title}>
          {weekdays.map((name) => (
            <li key={name} aria-hidden="true" className="text-center text-xs text-ink-muted">
              {name}
            </li>
          ))}
          {Array.from({ length: lead }, (_, index) => (
            <li key={`lead-${index}`} aria-hidden="true" />
          ))}
          {days.map((day) => {
            const value = day.mood ?? day.energy;
            const future = day.date > today;
            const label = [
              formatDay(day.date, 'full', i18n.language),
              day.mood !== null
                ? t('diary.moodValue', { value: day.mood })
                : day.energy !== null
                  ? t('diary.energyValue', { value: day.energy })
                  : t('diary.noMood'),
              day.trained ? t('diary.trained') : null,
              day.photos || day.notes ? t('diary.written') : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <li key={day.date}>
                <button
                  type="button"
                  disabled={future}
                  onClick={() => onOpen(day.date)}
                  aria-label={label}
                  className={`relative flex aspect-square w-full flex-col items-center justify-center rounded-lg border text-sm disabled:opacity-30 ${
                    day.date === today ? 'border-accent' : 'border-line'
                  } ${value !== null ? MOOD_FILL[value] : ''}`}
                >
                  <span aria-hidden="true">{Number(day.date.slice(8))}</span>
                  <span aria-hidden="true" className="flex h-1.5 gap-0.5">
                    {day.trained ? <i className="size-1.5 rounded-full bg-accent" /> : null}
                    {day.photos || day.notes ? (
                      <i className="size-1.5 rounded-full border border-ink-muted" />
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <p className="text-xs text-ink-muted">{t('diary.legend')}</p>
      </div>
    </section>
  );
}
